import { storeToRefs } from "pinia";
import { ref } from "vue";

import { type ContactList, type MediaRelease } from "shared/resources";
import {
  type SuggestedContacts,
  type SuggestedContactsValidate,
  ContactListSuggestedContacts,
  MediaReleaseSuggestedContacts,
} from "shared/services/suggestedContacts";
import useAIStore from "shared/stores/ai";

export default function useSuggestedContactsSuggestions() {
  const aiStore = useAIStore();

  const { aiSuggestedContactIds, aiSuggestedContactsFilter } =
    storeToRefs(aiStore);

  const contactList = ref<ContactList>();
  const loading = ref<boolean>(false);
  const mediaRelease = ref<MediaRelease>();
  const selectedIds = ref<string[]>([]);
  const total = ref<number>(0);
  const updating = ref<boolean>(false);
  const valid = ref<boolean>(false);

  let suggestedContacts:
    | SuggestedContacts<MediaRelease>
    | SuggestedContacts<ContactList>;

  async function add(type?: string) {
    updating.value = true;
    await suggestedContacts.add(selectedIds.value, type);
    aiSuggestedContactIds.value = selectedIds.value;
    selectedIds.value = [];
    updating.value = false;
  }

  function reset() {
    contactList.value = undefined;
    mediaRelease.value = undefined;
    selectedIds.value = [];
    total.value = 0;
    valid.value = false;
  }

  async function init() {
    reset();

    loading.value = true;

    if (aiSuggestedContactsFilter.value.suggestionsForMediaReleaseId) {
      suggestedContacts = new MediaReleaseSuggestedContacts();

      mediaRelease.value = await suggestedContacts.load(
        aiSuggestedContactsFilter.value.suggestionsForMediaReleaseId
      );
    } else if (aiSuggestedContactsFilter.value.suggestionsForContactListId) {
      suggestedContacts = new ContactListSuggestedContacts();

      contactList.value = await suggestedContacts.load(
        aiSuggestedContactsFilter.value.suggestionsForContactListId
      );
    } else {
      loading.value = false;

      return;
    }

    const validateResponse: SuggestedContactsValidate =
      await suggestedContacts.validate();

    valid.value = validateResponse.valid;
    total.value = validateResponse.total;
    loading.value = false;
  }

  return {
    contactList,
    loading,
    mediaRelease,
    selectedIds,
    total,
    updating,
    valid,

    init,
    add,
    reset,
  };
}
